import { useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import type { StorageMode } from '../../data/driver';
import { useData } from '../../data/hooks';
import { Button } from '../../ui/Button';
import { Segmented } from '../../ui/Segmented';
import { Sheet } from '../../ui/Sheet';
import { useSession } from '../auth/session';
import { SyncStatusLine } from '../shell/SyncStatusLine';
import { AboutSection } from './AboutSection';
import { ChangePasswordForm } from './ChangePasswordForm';
import { NavPinsEditor } from './NavPinsEditor';
import { getTheme, setTheme, type Theme } from './theme';
import { unsyncedTitle, useSignOut } from './useSignOut';

export function dataNote(mode: StorageMode, persisted: boolean | null): string {
  if (mode === 'memory') {
    return "This browser can't keep data on the device, so Tahi loads it from your account each time you open it.";
  }
  return persisted
    ? 'Your data is saved on this device and synced to your account.'
    : 'Your data is saved on this device and synced to your account. The browser may clear it if space runs low; your account keeps a copy.';
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-3 rounded-[24px] border border-divider bg-surface-2 p-4">
      <h2 className="text-xl font-bold">{title}</h2>
      {children}
    </section>
  );
}

export function SettingsScreen() {
  const session = useSession();
  const { mode, persisted, me } = useData();
  const signOut = useSignOut();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [theme, setThemeState] = useState<Theme>(getTheme);
  const [confirmAll, setConfirmAll] = useState(false);
  const isAdmin = me?.user.role === 'super_admin';

  return (
    <div className="flex flex-col gap-4 md:max-w-2xl">
      <h1 className="text-[26px] font-bold leading-tight">Settings</h1>

      <Section title="Appearance">
        <Segmented<Theme>
          label="Theme"
          value={theme}
          options={[
            { value: 'dark', label: 'Dark' },
            { value: 'light', label: 'Light' },
          ]}
          onChange={(t) => {
            setTheme(t);
            setThemeState(t);
          }}
        />
        <p className="text-xs text-muted">Light is easier to read in bright sunlight.</p>
      </Section>

      <Section title="Bottom bar">
        <NavPinsEditor />
      </Section>

      <Section title="Account">
        <p className="text-sm text-muted">Signed in as {session.user?.email ?? me?.user.email}</p>
        <ChangePasswordForm />
        <Button disabled={signOut.busy} onClick={() => void signOut.start('this_device')}>
          Sign out
        </Button>
        <Button variant="destructive" disabled={signOut.busy} onClick={() => setConfirmAll(true)}>
          Sign out of all devices
        </Button>
        {signOut.error ? (
          <p role="alert" className="text-sm text-destructive">
            {signOut.error}
          </p>
        ) : null}
        <p className="text-xs text-muted">
          Signing out removes Tahi's data from this device. Your account keeps it.
        </p>
      </Section>

      {isAdmin ? (
        <Section title="Admin">
          <Link to="/admin" className="inline-flex min-h-11 items-center text-primary underline">
            Admin console
          </Link>
        </Section>
      ) : null}

      <Section title="Your data">
        <p className="text-sm">{dataNote(mode, persisted)}</p>
        <SyncStatusLine />
        <p className="text-sm text-muted">Turning a module off hides it. Nothing is deleted.</p>
      </Section>

      <AboutSection />

      <Sheet
        open={confirmAll}
        onOpenChange={setConfirmAll}
        title="Sign out of all devices?"
        description="You'll need to sign in again on every phone and computer, including this one."
      >
        <div className="flex gap-2">
          <Button
            variant="destructive"
            onClick={() => {
              setConfirmAll(false);
              void signOut.start('everywhere');
            }}
          >
            Sign out everywhere
          </Button>
          <Button onClick={() => setConfirmAll(false)}>Cancel</Button>
        </div>
      </Sheet>

      <Sheet
        open={signOut.unsynced !== null}
        onOpenChange={(open) => {
          if (!open) signOut.cancel();
        }}
        title={unsyncedTitle(signOut.unsynced?.pending ?? 0)}
        description="If you sign out now, they're removed from this device and never reach your account. Connect to the internet to sync them first."
        initialFocus={cancelRef}
      >
        <div className="flex gap-2">
          <Button ref={cancelRef} variant="primary" onClick={signOut.cancel}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => signOut.unsynced && void signOut.finish(signOut.unsynced.kind)}
          >
            Sign out anyway
          </Button>
        </div>
      </Sheet>
    </div>
  );
}
